import { useState, useRef, useEffect } from "react";
import { useServiceEventListener } from "@/hooks/useService";
import { RPC_EVENT_FILE_UPDATED_IDENT, type FileUpdatedEvent } from "@bloomstack/apis/services/IFileStoreServicePublic";
import type { FileEntry } from "@bloomstack/apis/files";

// Debounce window before reloading previews (milliseconds)
const RELOAD_DEBOUNCE_MS = 2000;

/**
 * Subscribes to FILE_UPDATED events for a workspace and returns a reload key
 * that increments after a 2s debounce.
 *
 * Pass the returned value as a React `key` prop to any file viewer component to
 * force a remount (and therefore a fresh fetch / iframe reload) whenever a file
 * in the workspace changes. This gives all viewer types (URL-based, fetch-based,
 * and the HTML iframe) live-update behavior with a single mechanism.
 *
 * @param workspaceId - The workspace (chatroom) ID to watch, or undefined to disable.
 * @returns An incrementing reload key.
 */
export function useFilePreviewReload(workspaceId: string | undefined): number {
  const [reloadKey, setReloadKey] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Keep workspaceId in a ref so the event handler always sees the latest value
  // without re-subscribing on every render.
  const workspaceIdRef = useRef(workspaceId);

  useEffect(() => {
    workspaceIdRef.current = workspaceId;
  }, [workspaceId]);

  useServiceEventListener<FileUpdatedEvent>(
    RPC_EVENT_FILE_UPDATED_IDENT,
    (fileEntry: FileEntry) => {
      if (!workspaceIdRef.current) return;
      if (fileEntry.roomId !== workspaceIdRef.current) return;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setReloadKey((k) => k + 1), RELOAD_DEBOUNCE_MS);
    },
    [workspaceId]
  );

  // Clear pending debounce timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return reloadKey;
}
